import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { useUser } from '../../utils/userContext';
import { ToastContainer, toast } from 'react-toastify';
import useHandleErrors from '../../utils/user.utils';
import { Modal, Button } from 'react-bootstrap';
import useConfiguration from '../../utils/ConfigurationContext';

const UpdateWelcomeKit = ({ employeeId: propEmployeeId, isModal = false, onClose }) => {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("Choose file");
  const [docFile, setDocFile] = useState(null);
  const [docFileName, setDocFileName] = useState("Choose file");
  const [verificationFile, setVerificationFile] = useState(null);
  const [verificationFileName, setVerificationFileName] = useState("Choose file");
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user } = useUser();
  const [offerLetterValid, setOfferLetterValid] = useState(true);
  const [verificationValid, setVerificationValid] = useState(true);
  const handleErrors = useHandleErrors();
  const { employeeId: urlEmployeeId } = useParams();
  const config = useConfiguration();
  const token = localStorage.getItem("token");
  const [existingData, setExistingData] = useState(null);
  const [employeeInfo, setEmployeeInfo] = useState(null);
  const employeeId = propEmployeeId || urlEmployeeId;
  const [searchEmployeeId, setSearchEmployeeId] = useState(employeeId || '');
  const [currentEmployeeId, setCurrentEmployeeId] = useState(employeeId || '');
  const [showWelcomeKitData, setShowWelcomeKitData] = useState(false);
  const [hasData, setHasData] = useState(false);

  const initialState = {
    offerLetter: false,
    employeeVerification: false,
    biometricAccess: false,
    assetConfirmation: false,
    employeeDocuments: false,
    welcomeKit: false,
    leavesAssigned: false,
    offerLetterFile: null,
    employeeDocumentsFile: null,
    employeeVerificationFile: null,
    ActiveFlag: "Y",
    UpdatedBy: user?.FirstName,
  };
  const [formData, setFormData] = useState(initialState);
  const navigate = useNavigate();

  useEffect(() => {
    const preloading = document.getElementById('preloading');
    setTimeout(() => {
      if (preloading) {
        preloading.style.display = 'none';
      }
    }, 400);
  }, []);

  // Auto-fetch if employeeId is provided as prop or in URL
  useEffect(() => {
    const effectiveEmployeeId = propEmployeeId || urlEmployeeId;
    if (effectiveEmployeeId) {
      fetchWelcomeKitData(effectiveEmployeeId);
    }
  }, [propEmployeeId, urlEmployeeId, token]);

  // Function to get file name from URL
  const getFileNameFromUrl = (url) => {
    try {
      const urlParts = url.split('/');
      const fileNameWithParams = urlParts[urlParts.length - 1];
      const fileName = fileNameWithParams.split('?')[0];
      return decodeURIComponent(fileName);
    } catch (error) {
      return 'Document';
    }
  };

  // Function to open document in new tab
  const openDocumentInNewTab = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Function to redirect to leave balance page
  const handleLeaveBalanceRedirect = () => {
    navigate('/LeaveBalance');
  };

  // Fetch existing welcome kit data
  const fetchWelcomeKitData = async (employeeId) => {
    try {
      setLoading(true);
      const path = `/getwelcomeKit?employeeId=${employeeId}`;
      const URL = process.env.REACT_APP_BE_URL + path;
      const response = await axios.get(URL, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200 && response.data) {
        // Check if there's actual data
        const hasWelcomeKitData = response.data.length > 0 || Object.keys(response.data).length > 0;
        setHasData(hasWelcomeKitData);
        if (hasWelcomeKitData) {
          setExistingData(response.data);
          setEmployeeInfo(response.data.employeeInfo || response.data);
          setShowWelcomeKitData(true);
          setCurrentEmployeeId(employeeId);
          
          // Pre-populate form with existing data
          setFormData(prev => ({
            ...prev,
            offerLetter: response.data.OfferLetter || false,
            employeeVerification: response.data.EmployeeVerification || false,
            biometricAccess: response.data.BiometricAccess === "true" || response.data.BiometricAccess === true,
            assetConfirmation: response.data.AssetHandoverConfirmation === "true" || response.data.AssetHandoverConfirmation === true,
            employeeDocuments: Array.isArray(response.data.EmployeeDocuments) && response.data.EmployeeDocuments.length > 0,
            welcomeKit: response.data.WelcomeKit === "true" || response.data.WelcomeKit === true,
            leavesAssigned: response.data.LeaveCredit === "true" || response.data.LeaveCredit === true,
          }));
          if (response.data.EmployeeVerificationFileName) {
            setVerificationFileName(response.data.EmployeeVerificationFileName);
          }
        } else {
          setShowWelcomeKitData(true);
        }
      } else {
        setHasData(false);
        setShowWelcomeKitData(true);
      }
    } catch (error) {
      setHasData(false);
      setShowWelcomeKitData(true);
      console.error("Error fetching welcome kit data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (searchEmployeeId.trim()) {
      fetchWelcomeKitData(searchEmployeeId.trim());
    } else {
      toast.error("Please enter an Employee ID", {
        position: toast.POSITION.TOP_CENTER,
      });
    }
  };

  const handleSearchInputChange = (e) => {
    setSearchEmployeeId(e.target.value);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleToggleChange = (event) => {
    const { name, checked } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: checked,
    }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (allowedTypes.includes(selectedFile.type)) {
        setFile(selectedFile);
        setFileName(selectedFile.name);
        setOfferLetterValid(true);
      } else {
        toast.error("Only .doc and .pdf files are allowed.", {
          position: toast.POSITION.TOP_CENTER,
        });
        setOfferLetterValid(false);
      }
    }
  };

  const handleVerificationFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (allowedTypes.includes(selectedFile.type)) {
        setVerificationFile(selectedFile);
        setVerificationFileName(selectedFile.name);
        setVerificationValid(true);
      } else {
        toast.error("Only .doc and .pdf files are allowed.", {
          position: toast.POSITION.TOP_CENTER,
        });
        setVerificationValid(false);
      }
    }
  };

  const handleIdChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (allowedTypes.includes(selectedFile.type)) {
        setDocFile(selectedFile);
        setDocFileName(selectedFile.name);
      } else {
        toast.error("Only .doc and .pdf files are allowed.", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    }
  };

  const handleSubmit = async () => {
    if (!currentEmployeeId) {
      toast.error("Please search for an employee first", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }

    setLoading(true);
    const payload = new FormData();
    payload.append('EmployeeId', currentEmployeeId);
    payload.append('BiometricAccess', formData.biometricAccess);
    payload.append('AssetHandoverConfirmation', formData.assetConfirmation);
    payload.append('WelcomeKit', formData.welcomeKit);
    payload.append('LeaveCredit', formData.leavesAssigned);
    payload.append('OfferLetter', formData.offerLetter);
    payload.append('EmployeeVerification', formData.employeeVerification);

    // Only append files if new ones are selected
    if (file) {
      payload.append('OfferLetter', file);
    }
    if (docFile) {
      payload.append('EmployeeDocuments', docFile);
    }
    if (verificationFile) {
      payload.append('EmployeeVerification', verificationFile);
    }

    payload.append('ActiveFlag', "Y");
    payload.append('UpdatedBy', user?.FirstName);

    try {
      const path = "/welcomeKit";
      const URL = process.env.REACT_APP_BE_URL + path;
      
      // Use PUT for both creating and updating data
      const method = hasData ? 'put' : 'put';
      const response = await axios[method](URL, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      
      if (response.status === 200) {
        if (isModal) {
          toast.success(`Welcome Kit ${hasData ? 'updated' : 'created'} successfully!`, {
            position: toast.POSITION.TOP_CENTER,
          });
          // Refresh the data after successful operation
          fetchWelcomeKitData(currentEmployeeId);
        } else {
          setShowModal(true);
          fetchWelcomeKitData(currentEmployeeId);
        }
      } else {
        handleErrors(response);
      }
    } catch (error) {
      if (error.response) {
        handleErrors(error.response);
      } else {
        toast.error("Something went wrong. Please try again later.", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleReset = () => {
    setFormData(initialState);
    setFile(null);
    setDocFile(null);
    setVerificationFile(null);
    setFileName("Choose file");
    setDocFileName("Choose file");
    setVerificationFileName("Choose file");
    setExistingData(null);
    setEmployeeInfo(null);
    setShowWelcomeKitData(false);
    setCurrentEmployeeId('');
    setSearchEmployeeId('');
    setHasData(false);
  };

  // Render content based on state
  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center p-4">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      );
    }

    if (showWelcomeKitData && !hasData) {
      return (
        <div className={isModal ? "" : "content-page"}>
          {loading && (
            <div id="preloading">
              <span className="loader"></span>
            </div>
          )}
          <ToastContainer />
          <div className={isModal ? "" : "container-fluid"}>
            {!isModal && (
              <div className="row mb-2">
                <div className="col">
                  <h3 className="title mb-2">Add Welcome Kit</h3>
                </div>
                <div className="col-auto">
                  <Link
                    to={'/Employees'}
                    data-extra-toggle="tab"
                    data-target="#organisation-pan"
                    className="btn btn-primary mt-lg-0 mt-4"
                  >
                    <i className="ri-user-star-line"></i> View All Employees
                  </Link>
                </div>
              </div>
            )}

            {/* Search Section */}
            {!isModal && (
              <div className="row mb-4">
                <div className="col-lg-12">
                  <div className="card">
                    <div className="card-body">
                      <h5 className="card-title">Search Employee</h5>
                      <div className="row align-items-center">
                        <div className="col-md-8">
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter Employee ID"
                              value={searchEmployeeId}
                              onChange={handleSearchInputChange}
                              onKeyPress={handleKeyPress}
                            />
                            <div className="input-group-append">
                              <button
                                className="btn btn-primary"
                                type="button"
                                onClick={handleSearch}
                                disabled={loading}
                              >
                                <i className="ri-search-line"></i> Search
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* No Data Info */}
            <div className="row mb-4">
              <div className="col-lg-12">
                <div className="alert alert-info">
                  <i className="ri-information-line"></i>
                  <strong> No Welcome Kit Data Found</strong>
                  <p className="mb-0">No welcome kit data found for Employee ID: <strong>{currentEmployeeId}</strong>. You can create a new welcome kit entry below.</p>
                </div>
              </div>
            </div>

            {/* Add Welcome Kit Form */}
            <div className="row">
              <div className="col-lg-12">
                <div className="tab-pane" id="welcome-kit" role="tabpanel">
                  <div className="row">
                    <div className="col-lg-12">
                      <div className="card mb-4">
                        <div className="card-body">
                          <div className="row">
                            <div className="col">
                              <h5 className="title mt-1 mb-0">Add Welcome Kit</h5>
                              <small className="text-muted">
                                Employee ID: <strong>{currentEmployeeId}</strong>
                              </small>
                            </div>
                          </div>
                          <div className="row align-items-center justify-content-center mt-4">
                            <div className="col-sm-12 col-md-8 col-lg-7">
                              <div className="container-fluid row-rounded">
                                <div className="row px-2 py-0">
                                  <div className="input-group mb-0">
                                    <input style={{ borderRadius: "20px" }}
                                      type="search"
                                      className="form-control"
                                      value={currentEmployeeId}
                                      readOnly
                                    />
                                  </div>
                                </div>
                              </div>
                              
                              <div className="container-fluid row-rounded">
                                <div className="row">
                                  <div className="col-auto">
                                    <div className="circle-icon-box">
                                      <i className="ri-article-line"></i>
                                    </div>
                                  </div>
                                  <div className="col">
                                    Offer Letter{' '}
                                    <small className="form-text text-muted">
                                      Upload if yes
                                    </small>
                                  </div>
                                  <div className="col-2">
                                    <div className="button-cover">
                                      <div className="btn-switch button r">
                                        <input
                                          type='checkbox'
                                          className="checkbox"
                                          name="offerLetter"
                                          accept=".pdf,.doc,application/msword"
                                          checked={formData.offerLetter}
                                          onChange={handleToggleChange}
                                        />
                                        <div className="knobs"></div>
                                        <div className="layer"></div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {formData.offerLetter && (
                                <div className="row">
                                  <div className="form-group col-md-12">
                                    <div className="input-group">
                                      <div className="custom-file">
                                        <input
                                          type="file"
                                          className="custom-file-input"
                                          id="offerLetterFile"
                                          name="offerLetterFile"
                                          accept=".pdf,.doc,application/msword"
                                          onChange={handleFileChange}
                                        />
                                        <label
                                          className="custom-file-label"
                                          htmlFor="offerLetterFile"
                                        >
                                          {fileName}
                                        </label>
                                        {!offerLetterValid && (
                                          <div className="invalid-feedback d-block">
                                            Only .doc and .pdf files are allowed.
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              <div className="container-fluid row-rounded">
                                <div className="row">
                                  <div className="col-auto">
                                    <div className="circle-icon-box">
                                      <i className="ri-verified-badge-line"></i>
                                    </div>
                                  </div>
                                  <div className="col">
                                    Employee Verification{' '}
                                    <small className="form-text text-muted">
                                      Upload if yes
                                    </small>
                                  </div>
                                  <div className="col-2">
                                    <div className="button-cover">
                                      <div className="btn-switch button r">
                                        <input
                                          type='checkbox'
                                          className="checkbox"
                                          name="employeeVerification"
                                          accept=".pdf,.doc,application/msword"
                                          checked={formData.employeeVerification}
                                          onChange={handleToggleChange}
                                        />
                                        <div className="knobs"></div>
                                        <div className="layer"></div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {formData.employeeVerification && (
                                <div className="row">
                                  <div className="form-group col-md-12">
                                    <div className="input-group">
                                      <div className="custom-file">
                                        <input
                                          type="file"
                                          className="custom-file-input"
                                          id="employeeVerificationFile"
                                          name="employeeVerificationFile"
                                          accept=".pdf,.doc,application/msword"
                                          onChange={handleVerificationFileChange}
                                        />
                                        <label
                                          className="custom-file-label"
                                          htmlFor="employeeVerificationFile"
                                        >
                                          {verificationFileName}
                                        </label>
                                        {!verificationValid && (
                                          <div className="invalid-feedback d-block">
                                            Only .doc and .pdf files are allowed.
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              <div className="container-fluid row-rounded">
                                <div className="row">
                                  <div className="col-auto">
                                    <div className="circle-icon-box">
                                      <i className="ri-fingerprint-line"></i>
                                    </div>
                                  </div>
                                  <div className="col">
                                    Biometric Access{" "}
                                    <small className="form-text text-muted">
                                      Captured successfully?
                                    </small>
                                  </div>
                                  <div className="col-2">
                                    <div className="button-cover">
                                      <div className="btn-switch button r">
                                        <input
                                          type="checkbox"
                                          className="checkbox"
                                          name="biometricAccess"
                                          checked={formData.biometricAccess}
                                          onChange={handleToggleChange}
                                        />
                                        <div className="knobs"></div>
                                        <div className="layer"></div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="container-fluid row-rounded">
                                <div className="row">
                                  <div className="col-auto">
                                    <div className="circle-icon-box">
                                      <i className="ri-calendar-check-line"></i>
                                    </div>
                                  </div>
                                  <div className="col">
                                    Leaves Assigned{" "}
                                    <small className="form-text text-muted">
                                      Mark as assigned after setting up leave balance
                                    </small>
                                    <div className="mt-1">
                                      <button
                                        className="btn btn-link btn-sm p-0 text-primary"
                                        onClick={handleLeaveBalanceRedirect}
                                        style={{ textDecoration: 'underline', fontSize: '12px' }}
                                      >
                                        <i className="ri-external-link-line"></i> Manage Leave Balance
                                      </button>
                                    </div>
                                  </div>
                                  <div className="col-2">
                                    <div className="button-cover">
                                      <div className="btn-switch button r">
                                        <input
                                          type="checkbox"
                                          className="checkbox"
                                          name="leavesAssigned"
                                          checked={formData.leavesAssigned}
                                          onChange={handleToggleChange}
                                        />
                                        <div className="knobs"></div>
                                        <div className="layer"></div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="container-fluid row-rounded">
                                <div className="row">
                                  <div className="col-auto">
                                    <div className="circle-icon-box">
                                      <i className="ri-hand-coin-line"></i>
                                    </div>
                                  </div>
                                  <div className="col">
                                    Asset Confirmation{" "}
                                    <small className="form-text text-muted">
                                      Handed over?
                                    </small>
                                  </div>
                                  <div className="col-2">
                                    <div className="button-cover">
                                      <div className="btn-switch button r">
                                        <input
                                          type="checkbox"
                                          className="checkbox"
                                          name="assetConfirmation"
                                          checked={formData.assetConfirmation}
                                          onChange={handleToggleChange}
                                        />
                                        <div className="knobs"></div>
                                        <div className="layer"></div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="container-fluid row-rounded">
                                <div className="row">
                                  <div className="col-auto">
                                    <div className="circle-icon-box">
                                      <i className="ri-file-text-line"></i>
                                    </div>
                                  </div>
                                  <div className="col">
                                    Employee Documents{" "}
                                    <small className="form-text text-muted">
                                      Upload if yes
                                    </small>
                                  </div>
                                  <div className="col-2">
                                    <div className="button-cover">
                                      <div className="btn-switch button r">
                                        <input
                                          type="checkbox"
                                          className="checkbox"
                                          name="employeeDocuments"
                                          accept=".pdf,.doc,application/msword"
                                          checked={formData.employeeDocuments}
                                          onChange={handleToggleChange}
                                        />
                                        <div className="knobs"></div>
                                        <div className="layer"></div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {formData.employeeDocuments && (
                                <div className="row">
                                  <div className="form-group col-md-12">
                                    <div className="input-group">
                                      <div className="custom-file">
                                        <input
                                          type="file"
                                          className="custom-file-input"
                                          id="employeeDocumentsFile"
                                          name="employeeDocumentsFile"
                                          accept=".pdf,.doc,application/msword"
                                          onChange={handleIdChange}
                                        />
                                        <label
                                          className="custom-file-label"
                                          htmlFor="employeeDocumentsFile"
                                        >
                                          {docFileName}
                                        </label>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              <div className="container-fluid row-rounded">
                                <div className="row">
                                  <div className="col-auto">
                                    <div className="circle-icon-box">
                                      <i className="ri-shapes-line"></i>
                                    </div>
                                  </div>
                                  <div className="col">
                                    Welcome Kit{" "}
                                    <small className="form-text text-muted">
                                      All distributed?
                                    </small>
                                  </div>
                                  <div className="col-2">
                                    <div className="button-cover">
                                      <div className="btn-switch button r">
                                        <input
                                          type="checkbox"
                                          className="checkbox"
                                          name="welcomeKit"
                                          checked={formData.welcomeKit}
                                          onChange={handleToggleChange}
                                        />
                                        <div className="knobs"></div>
                                        <div className="layer"></div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="row">
              <div className="col-lg-12 text-center">
                {!isModal && (
                  <button
                    className="btn btn-light me-2"
                    type="button"
                    onClick={handleReset}
                  >
                    <i className="ri-refresh-line"></i> Reset
                  </button>
                )}
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Creating...
                    </>
                  ) : (
                    <>
                      Create Welcome Kit <i className="ri-checkbox-circle-line"></i>
                    </>
                  )}
                </button>
                {isModal && (
                  <button className="btn btn-secondary ms-2" onClick={onClose}>
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Success Modal */}
          <Modal show={showModal} onHide={handleCloseModal} centered>
            <Modal.Header closeButton>
              <Modal.Title>Success</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="text-center">
                <i className="ri-checkbox-circle-line text-success" style={{ fontSize: '3rem' }}></i>
                <h5 className="mt-3">Welcome Kit Created Successfully!</h5>
                <p>The welcome kit has been created for employee {currentEmployeeId}.</p>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="primary" onClick={handleCloseModal}>
                Close
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      );
    }

    return (
      <div className={isModal ? "" : "content-page"}>
        {loading && (
          <div id="preloading">
            <span className="loader"></span>
          </div>
        )}
        <ToastContainer />
        <div className={isModal ? "" : "container-fluid"}>
          {!isModal && (
            <div className="row mb-2">
              <div className="col">
                <h3 className="title mb-2">Update Welcome Kit</h3>
              </div>
              <div className="col-auto">
                <Link
                  to={'/Employees'}
                  data-extra-toggle="tab"
                  data-target="#organisation-pan"
                  className="btn btn-primary mt-lg-0 mt-4"
                >
                  <i className="ri-user-star-line"></i> View All Employees
                </Link>
              </div>
            </div>
          )}

          {/* Search Section */}
          {!isModal && (
            <div className="row mb-4">
              <div className="col-lg-12">
                <div className="card">
                  <div className="card-body">
                    <h5 className="card-title">Search Employee</h5>
                    <div className="row align-items-center">
                      <div className="col-md-8">
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Employee ID"
                            value={searchEmployeeId}
                            onChange={handleSearchInputChange}
                            onKeyPress={handleKeyPress}
                          />
                          <div className="input-group-append">
                            <button
                              className="btn btn-primary"
                              type="button"
                              onClick={handleSearch}
                              disabled={loading}
                            >
                              <i className="ri-search-line"></i> Search
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Welcome Kit Data Display */}
          {showWelcomeKitData && existingData && hasData && (
            <div className="row mb-4">
              <div className="col-lg-12">
                <div className="card">
                  <div className="card-body">
                    <div className="row">
                      <div className="col">
                        <h5 className="card-title">Current Welcome Kit Status</h5>
                        {employeeInfo && (
                          <div className="mb-3">
                            <small className="text-muted">
                              ID: <strong>{currentEmployeeId}</strong>
                            </small>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="table-responsive">
                          <table className="table table-sm">
                            <tbody>
                              <tr>
                                <td><strong>Offer Letter:</strong></td>
                                <td>
                                  <span className={`badge ${existingData.OfferLetter ? 'badge-success' : 'badge-secondary'}`}>
                                    {existingData.OfferLetter ? 'Completed' : 'Pending'}
                                  </span>
                                  {existingData.OfferLetterFileName && (
                                    <small className="d-block text-muted mt-1">
                                      <a 
                                        href="#" 
                                        onClick={(e) => {
                                          e.preventDefault();
                                          openDocumentInNewTab(existingData.OfferLetter);
                                        }}
                                        className="text-info"
                                      >
                                        {getFileNameFromUrl(existingData.OfferLetter)}
                                      </a>
                                    </small>
                                  )}
                                </td>
                              </tr>
                              <tr>
                                <td><strong>Employee Verification:</strong></td>
                                <td>
                                  <span className={`badge ${existingData.EmployeeVerification ? 'badge-success' : 'badge-secondary'}`}>
                                    {existingData.EmployeeVerification ? 'Completed' : 'Pending'}
                                  </span>
                                  {existingData.EmployeeVerificationFileName && (
                                    <small className="d-block text-muted mt-1">
                                      <a 
                                        href="#" 
                                        onClick={(e) => {
                                          e.preventDefault();
                                          openDocumentInNewTab(existingData.EmployeeVerification);
                                        }}
                                        className="text-info"
                                      >
                                        {getFileNameFromUrl(existingData.EmployeeVerification)}
                                      </a>
                                    </small>
                                  )}
                                </td>
                              </tr>
                              <tr>
                                <td><strong>Biometric Access:</strong></td>
                                <td>
                                  <span className={`badge ${existingData.BiometricAccess === 'true' || existingData.BiometricAccess === true ? 'badge-success' : 'badge-secondary'}`}>
                                    {existingData.BiometricAccess === 'true' || existingData.BiometricAccess === true ? 'Completed' : 'Pending'}
                                  </span>
                                </td>
                              </tr>
                              <tr>
                                <td><strong>Asset Confirmation:</strong></td>
                                <td>
                                  <span className={`badge ${existingData.AssetHandoverConfirmation === 'true' || existingData.AssetHandoverConfirmation === true ? 'badge-success' : 'badge-secondary'}`}>
                                    {existingData.AssetHandoverConfirmation === 'true' || existingData.AssetHandoverConfirmation === true ? 'Completed' : 'Pending'}
                                  </span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="table-responsive">
                          <table className="table table-sm">
                            <tbody>
                              <tr>
                                <td><strong>Employee Documents:</strong></td>
                                <td>
                                  <span className={`badge ${Array.isArray(existingData.EmployeeDocuments) && existingData.EmployeeDocuments.length > 0 ? 'badge-success' : 'badge-secondary'}`}>
                                    {Array.isArray(existingData.EmployeeDocuments) && existingData.EmployeeDocuments.length > 0 ? 'Completed' : 'Pending'}
                                  </span>
                                  {Array.isArray(existingData.EmployeeDocuments) && existingData.EmployeeDocuments.length > 0 && (
                                    <div className="mt-1">
                                      {existingData.EmployeeDocuments.map((doc, index) => (
                                        <small key={index} className="d-block text-muted">
                                          <a 
                                            href="#" 
                                            onClick={(e) => {
                                              e.preventDefault();
                                              openDocumentInNewTab(doc);
                                            }}
                                            className="text-info"
                                          >
                                            {getFileNameFromUrl(doc)}
                                          </a>
                                        </small>
                                      ))}
                                    </div>
                                  )}
                                </td>
                              </tr>
                              <tr>
                                <td><strong>Welcome Kit:</strong></td>
                                <td>
                                  <span className={`badge ${existingData.WelcomeKit === 'true' || existingData.WelcomeKit === true ? 'badge-success' : 'badge-secondary'}`}>
                                    {existingData.WelcomeKit === 'true' || existingData.WelcomeKit === true ? 'Completed' : 'Pending'}
                                  </span>
                                </td>
                              </tr>
                              <tr>
                                <td><strong>Leaves Assigned:</strong></td>
                                <td>
                                  <span className={`badge ${existingData.LeaveCredit === 'true' || existingData.LeaveCredit === true ? 'badge-success' : 'badge-secondary'}`}>
                                    {existingData.LeaveCredit === 'true' || existingData.LeaveCredit === true ? 'Completed' : 'Pending'}
                                  </span>
                                  {(existingData.LeaveCredit === 'true' || existingData.LeaveCredit === true) && (
                                    <small className="d-block text-muted mt-1">
                                      <a 
                                        href="#" 
                                        onClick={(e) => {
                                          e.preventDefault();
                                          handleLeaveBalanceRedirect();
                                        }}
                                        className="text-info"
                                      >
                                        View Leave Balance
                                      </a>
                                    </small>
                                  )}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Update Form */}
          {showWelcomeKitData && (
            <div className="row">
              <div className="col-lg-12">
                <div className="tab-pane" id="welcome-kit" role="tabpanel">
                  <div className="row">
                    <div className="col-lg-12">
                      <div className="card mb-4">
                        <div className="card-body">
                          <div className="row">
                            <div className="col">
                              <h5 className="title mt-1 mb-0">Update Welcome Kit</h5>
                              {employeeInfo && (
                                <small className="text-muted">
                                  {/* Employee: {employeeInfo.EmployeeName} | Email: {employeeInfo.OfficialEmail} */}
                                </small>
                              )}
                            </div>
                          </div>
                          <div className="row align-items-center justify-content-center mt-4">
                            <div className="col-sm-12 col-md-8 col-lg-7">
                              <div className="container-fluid row-rounded">
                                <div className="row px-2 py-0">
                                  <div className="input-group mb-0">
                                    <input style={{ borderRadius: "20px" }}
                                      type="search"
                                      className="form-control"
                                      value={currentEmployeeId}
                                      readOnly
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="container-fluid row-rounded">
                                <div className="row">
                                  <div className="col-auto">
                                    <div className="circle-icon-box">
                                      <i className="ri-article-line"></i>
                                    </div>
                                  </div>
                                  <div className="col">
                                    Offer Letter{' '}
                                    <small className="form-text text-muted">
                                      Upload if yes
                                    </small>
                                    {existingData?.OfferLetterFileName && (
                                      <small className="d-block text-info">
                                        Current: {existingData.OfferLetterFileName}
                                      </small>
                                    )}
                                  </div>
                                  <div className="col-2">
                                    <div className="button-cover">
                                      <div className="btn-switch button r">
                                        <input
                                          type='checkbox'
                                          className="checkbox"
                                          name="offerLetter"
                                          accept=".pdf,.doc,application/msword"
                                          checked={formData.offerLetter}
                                          onChange={handleToggleChange}
                                        />
                                        <div className="knobs"></div>
                                        <div className="layer"></div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {formData.offerLetter && (
                                <div className="row">
                                  <div className="form-group col-md-12">
                                    <div className="input-group">
                                      <div className="custom-file">
                                        <input
                                          type="file"
                                          className="custom-file-input"
                                          id="offerLetterFile"
                                          name="offerLetterFile"
                                          accept=".pdf,.doc,application/msword"
                                          onChange={handleFileChange}
                                        />
                                        <label
                                          className="custom-file-label"
                                          htmlFor="offerLetterFile"
                                        >
                                          {fileName}
                                        </label>
                                        {!offerLetterValid && (
                                          <div className="invalid-feedback d-block">
                                            Only .doc and .pdf files are allowed.
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              <div className="container-fluid row-rounded">
                                <div className="row">
                                  <div className="col-auto">
                                    <div className="circle-icon-box">
                                      <i className="ri-verified-badge-line"></i>
                                    </div>
                                  </div>
                                  <div className="col">
                                    Employee Verification{' '}
                                    <small className="form-text text-muted">
                                      Upload if yes
                                    </small>
                                    {existingData?.EmployeeVerificationFileName && (
                                      <small className="d-block text-info">
                                        Current: {existingData.EmployeeVerificationFileName}
                                      </small>
                                    )}
                                  </div>
                                  <div className="col-2">
                                    <div className="button-cover">
                                      <div className="btn-switch button r">
                                        <input
                                          type='checkbox'
                                          className="checkbox"
                                          name="employeeVerification"
                                          accept=".pdf,.doc,application/msword"
                                          checked={formData.employeeVerification}
                                          onChange={handleToggleChange}
                                        />
                                        <div className="knobs"></div>
                                        <div className="layer"></div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {formData.employeeVerification && (
                                <div className="row">
                                  <div className="form-group col-md-12">
                                    <div className="input-group">
                                      <div className="custom-file">
                                        <input
                                          type="file"
                                          className="custom-file-input"
                                          id="employeeVerificationFile"
                                          name="employeeVerificationFile"
                                          accept=".pdf,.doc,application/msword"
                                          onChange={handleVerificationFileChange}
                                        />
                                        <label
                                          className="custom-file-label"
                                          htmlFor="employeeVerificationFile"
                                        >
                                          {verificationFileName}
                                        </label>
                                        {!verificationValid && (
                                          <div className="invalid-feedback d-block">
                                            Only .doc and .pdf files are allowed.
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              <div className="container-fluid row-rounded">
                                <div className="row">
                                  <div className="col-auto">
                                    <div className="circle-icon-box">
                                      <i className="ri-fingerprint-line"></i>
                                    </div>
                                  </div>
                                  <div className="col">
                                    Biometric Access{" "}
                                    <small className="form-text text-muted">
                                      Captured successfully?
                                    </small>
                                  </div>
                                  <div className="col-2">
                                    <div className="button-cover">
                                      <div className="btn-switch button r">
                                        <input
                                          type="checkbox"
                                          className="checkbox"
                                          name="biometricAccess"
                                          checked={formData.biometricAccess}
                                          onChange={handleToggleChange}
                                        />
                                        <div className="knobs"></div>
                                        <div className="layer"></div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="container-fluid row-rounded">
                                <div className="row">
                                  <div className="col-auto">
                                    <div className="circle-icon-box">
                                      <i className="ri-calendar-check-line"></i>
                                    </div>
                                  </div>
                                  <div className="col">
                                    Leaves Assigned{" "}
                                    <small className="form-text text-muted">
                                      Mark as assigned after setting up leave balance
                                    </small>
                                    <div className="mt-1">
                                      <button
                                        className="btn btn-link btn-sm p-0 text-primary"
                                        onClick={handleLeaveBalanceRedirect}
                                        style={{ textDecoration: 'underline', fontSize: '12px' }}
                                      >
                                        <i className="ri-external-link-line"></i> Manage Leave Balance
                                      </button>
                                    </div>
                                  </div>
                                  <div className="col-2">
                                    <div className="button-cover">
                                      <div className="btn-switch button r">
                                        <input
                                          type="checkbox"
                                          className="checkbox"
                                          name="leavesAssigned"
                                          checked={formData.leavesAssigned}
                                          onChange={handleToggleChange}
                                        />
                                        <div className="knobs"></div>
                                        <div className="layer"></div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="container-fluid row-rounded">
                                <div className="row">
                                  <div className="col-auto">
                                    <div className="circle-icon-box">
                                      <i className="ri-hand-coin-line"></i>
                                    </div>
                                  </div>
                                  <div className="col">
                                    Asset Confirmation{" "}
                                    <small className="form-text text-muted">
                                      Handed over?
                                    </small>
                                  </div>
                                  <div className="col-2">
                                    <div className="button-cover">
                                      <div className="btn-switch button r">
                                        <input
                                          type="checkbox"
                                          className="checkbox"
                                          name="assetConfirmation"
                                          checked={formData.assetConfirmation}
                                          onChange={handleToggleChange}
                                        />
                                        <div className="knobs"></div>
                                        <div className="layer"></div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="container-fluid row-rounded">
                                <div className="row">
                                  <div className="col-auto">
                                    <div className="circle-icon-box">
                                      <i className="ri-file-text-line"></i>
                                    </div>
                                  </div>
                                  <div className="col">
                                    Employee Documents{" "}
                                    <small className="form-text text-muted">
                                      Upload if yes
                                    </small>
                                    {Array.isArray(existingData?.EmployeeDocuments) && existingData.EmployeeDocuments.length > 0 && (
                                      <div className="mt-1">
                                        <small className="d-block text-info">
                                          Current Documents ({existingData.EmployeeDocuments.length}):
                                        </small>
                                        {existingData.EmployeeDocuments.map((docUrl, index) => (
                                          <small key={index} className="d-block">
                                            <button
                                              className="btn btn-link btn-sm p-0 text-primary"
                                              onClick={() => openDocumentInNewTab(docUrl)}
                                              style={{ textDecoration: 'underline', fontSize: '11px' }}
                                            >
                                              <i className="ri-external-link-line"></i> {getFileNameFromUrl(docUrl)}
                                            </button>
                                          </small>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  <div className="col-2">
                                    <div className="button-cover">
                                      <div className="btn-switch button r">
                                        <input
                                          type="checkbox"
                                          className="checkbox"
                                          name="employeeDocuments"
                                          accept=".pdf,.doc,application/msword"
                                          checked={formData.employeeDocuments}
                                          onChange={handleToggleChange}
                                        />
                                        <div className="knobs"></div>
                                        <div className="layer"></div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {formData.employeeDocuments && (
                                <div className="row">
                                  <div className="form-group col-md-12">
                                    <div className="input-group">
                                      <div className="custom-file">
                                        <input
                                          type="file"
                                          className="custom-file-input"
                                          id="employeeDocumentsFile"
                                          name="employeeDocumentsFile"
                                          accept=".pdf,.doc,application/msword"
                                          onChange={handleIdChange}
                                        />
                                        <label
                                          className="custom-file-label"
                                          htmlFor="employeeDocumentsFile"
                                        >
                                          {docFileName}
                                        </label>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              <div className="container-fluid row-rounded">
                                <div className="row">
                                  <div className="col-auto">
                                    <div className="circle-icon-box">
                                      <i className="ri-shapes-line"></i>
                                    </div>
                                  </div>
                                  <div className="col">
                                    Welcome Kit{" "}
                                    <small className="form-text text-muted">
                                      All distributed?
                                    </small>
                                  </div>
                                  <div className="col-2">
                                    <div className="button-cover">
                                      <div className="btn-switch button r">
                                        <input
                                          type="checkbox"
                                          className="checkbox"
                                          name="welcomeKit"
                                          checked={formData.welcomeKit}
                                          onChange={handleToggleChange}
                                        />
                                        <div className="knobs"></div>
                                        <div className="layer"></div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      {showModal && (
                        <div
                          className="modal fade show d-block"
                          tabIndex="-1"
                          role="dialog"
                          aria-labelledby="successModalLabel"
                          aria-hidden="true"
                        >
                          <div
                            className="modal-dialog modal-sm modal-dialog-centered"
                            role="document"
                          >
                            <div className="modal-content shadow success-modal">
                              <div className="modal-header py-2">
                                <h6
                                  className="modal-title"
                                  id="successModalModalLabel"
                                >
                                  Success!
                                </h6>
                                <button
                                  className="close"
                                  type="button"
                                  aria-label="Close"
                                  onClick={handleCloseModal}
                                >
                                  <span aria-hidden="true">
                                    <i className="ri-close-circle-fill font-size-20"></i>
                                  </span>
                                </button>
                              </div>
                              <div className="modal-body pb-5">
                                <div className="row">
                                  <div className="col-12 text-success text-center">
                                    <i className="ri-checkbox-circle-fill"></i>
                                  </div>
                                  <div className="col-12 text-center">
                                    Welcome Kit updated successfully.
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

          {/* Action Buttons for Modal */}
          {showWelcomeKitData && hasData && isModal && (
            <div className="row mt-3">
              <div className="col-lg-12 text-center">
                <button
                  className="btn btn-light me-2"
                  type="button"
                  onClick={handleReset}
                >
                  <i className="ri-refresh-line"></i> Reset
                </button>
                <button
                  className="btn btn-primary me-2"
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Updating...
                    </>
                  ) : (
                    <>
                      Update Welcome Kit <i className="ri-checkbox-circle-line"></i>
                    </>
                  )}
                </button>
                <button className="btn btn-secondary" onClick={onClose}>
                  Cancel
                </button>
              </div>
            </div>
          )}

        {/* Success Modal */}
        <Modal show={showModal} onHide={handleCloseModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Success</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center">
              <i className="ri-checkbox-circle-line text-success" style={{ fontSize: '3rem' }}></i>
              <h5 className="mt-3">Welcome Kit Updated Successfully!</h5>
              <p>The welcome kit has been updated for employee {currentEmployeeId}.</p>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={handleCloseModal}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  };

  return renderContent();
};

export default UpdateWelcomeKit;